import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { get } from '@/api/client';
import { type Post } from '@/components/organisms/PostCard';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Avatar } from '@/components/atoms/Avatar';
import { BoardTabs } from '@/components/organisms/BoardTabs';
import { AdBanner } from '@/components/ads/AdBanner';
import { COIN_NAMES_KR, stripKrw } from '@/constants/coins';
import { timeAgo } from '@/lib/format';

type Period = 'all' | 'today' | 'week' | 'month';

const PERIOD_TABS: Array<{ key: Period; label: string }> = [
  { key: 'all', label: '전체' },
  { key: 'today', label: '오늘' },
  { key: 'week', label: '이번주' },
  { key: 'month', label: '이번달' },
];

/** ticker 있으면 종목 한글명 태그 표시 — BoardPage 와 동일 정책 */
function postTag(p: Post): string | null {
  if (p.ticker) {
    const kr = COIN_NAMES_KR[p.ticker];
    if (kr) return kr;
    return stripKrw(p.ticker);
  }
  return null;
}

export function PopularBoardPage() {
  const [period, setPeriod] = useState<Period>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['posts', 'popular', period],
    queryFn: () =>
      get<{ posts: Post[] }>(`/posts?sort=hot&limit=50&period=${period}`),
    staleTime: 60_000,
  });
  const posts = data?.posts ?? [];

  return (
    <>
      <BoardTabs activeSlug="__popular__" />
      <div className="container-base pb-6">
        <div className="rounded-2xl border border-[var(--color-border)] bg-white p-6">
          <header className="mb-5">
            <h1 className="text-2xl font-extrabold">실시간 인기글</h1>
            <p className="mt-1 text-sm text-[var(--color-text-sub)]">
              전체 게시판 통합 · 추천/조회/댓글 가중치 + 시간 감쇠
            </p>
          </header>

          {/* 기간 필터 — BoardPage 정렬 segmented 와 동일 톤 */}
          <div className="mb-4 flex overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] sm:inline-flex">
            {PERIOD_TABS.map((t, i) => (
              <button
                key={t.key}
                onClick={() => setPeriod(t.key)}
                className={`flex-1 px-3.5 py-1.5 text-sm font-medium transition-colors sm:flex-none ${
                  i > 0 ? 'border-l border-[var(--color-border)]' : ''
                } ${
                  period === t.key
                    ? 'bg-[var(--color-primary)] text-white'
                    : 'text-[var(--color-text-sub)] hover:bg-white hover:text-[var(--color-text)]'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : posts.length > 0 ? (
            <>
              {/* 컬럼 헤더 — PC 전용, BoardPage 와 동일 너비 */}
              <div className="hidden items-center gap-3 border-b border-[var(--color-border)] py-2 text-sm text-gray-400 sm:flex">
                <span className="min-w-0 flex-1">제목</span>
                <span className="block min-w-[80px] max-w-[80px] text-left">게시판</span>
                <span className="block min-w-[168px] max-w-[168px] text-left">작성자</span>
                <span className="inline-flex min-w-[56px] max-w-[56px] justify-end">조회수</span>
                <span className="inline-flex min-w-[44px] max-w-[44px] justify-end">추천</span>
                <span className="inline-flex min-w-[44px] max-w-[44px] justify-end">비추천</span>
                <span className="inline-flex min-w-[64px] justify-end">작성일</span>
              </div>
              <ul>
                {posts.map((p) => {
                  const tag = postTag(p);
                  const views = Math.min(99999, p.view_count ?? 0);
                  const likes = Math.min(9999, p.like_count ?? 0);
                  const dislikes = Math.min(9999, p.dislike_count ?? 0);
                  return (
                    <li
                      key={p.id}
                      className="border-b border-[var(--color-border)] last:border-b-0"
                    >
                      <Link
                        to={`/post/${p.id}`}
                        className="flex items-center gap-3 py-3 hover:bg-[var(--color-bg)]/60"
                      >
                        <span className="flex min-w-0 flex-1 items-center gap-2">
                          {tag && (
                            <span className="inline-flex h-5 shrink-0 items-center rounded bg-emerald-100 px-2 text-xs font-semibold leading-none text-emerald-700">
                              {tag}
                            </span>
                          )}
                          <span className="min-w-0 flex-1 truncate text-sm font-medium">
                            {p.title}
                            {p.comment_count > 0 && (
                              <span className="ml-1 font-semibold text-emerald-600">
                                [{p.comment_count}]
                              </span>
                            )}
                          </span>
                        </span>
                        {/* 작성자 + 통계 — PC 전용 */}
                        <div className="hidden shrink-0 items-center gap-3 text-xs text-[var(--color-text-sub)] sm:flex">
                          <span className="block min-w-[80px] max-w-[80px] truncate">
                            {p.board_name ?? '-'}
                          </span>
                          <div className="flex min-w-[168px] max-w-[168px] items-center gap-1.5">
                            <Avatar
                              src={p.author_image ?? undefined}
                              alt={p.author_nickname}
                              size="xs"
                              userId={p.user_id}
                            />
                            <span className="block min-w-0 flex-1 truncate font-medium text-[var(--color-text)]">
                              {p.author_nickname}
                            </span>
                          </div>
                          <span className="inline-flex min-w-[56px] max-w-[56px] justify-end tabular-nums">
                            {views.toLocaleString('ko-KR')}
                          </span>
                          <span className="inline-flex min-w-[44px] max-w-[44px] justify-end tabular-nums">
                            {likes.toLocaleString('ko-KR')}
                          </span>
                          <span className="inline-flex min-w-[44px] max-w-[44px] justify-end tabular-nums">
                            {dislikes.toLocaleString('ko-KR')}
                          </span>
                          <span className="inline-flex min-w-[64px] justify-end tabular-nums">
                            {timeAgo(p.created_at)}
                          </span>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <div className="rounded-xl border border-dashed border-[var(--color-border)] py-16 text-center text-[var(--color-text-sub)]">
              해당 기간 인기글이 없어요.
            </div>
          )}
        </div>

        {/* 페이지 하단 광고 — 정형 디스플레이 (모바일 가변 높이 회피) */}
        <AdBanner slot="display-rect" className="mt-3 sm:mt-6" />
      </div>
    </>
  );
}
